// DEPLOY: /var/www/woody.wtf/frontend/src/chart/IndicatorSettings.js
// Settings for one indicator: its parameters, each line's colour / width / visibility,
// and band shading opacity. Desktop: panel beside the chart. Phone: slide-up sheet.
import React from 'react';
import { INDICATORS, studyLabel } from './indicators';

const S = {
  panel:  { position: 'absolute', top: '34px', right: '72px', width: '300px', maxHeight: 'calc(100% - 70px)', overflowY: 'auto', zIndex: 17, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', boxShadow: 'var(--shadow)', padding: '10px 12px' },
  sheet:  { position: 'fixed', left: 0, right: 0, bottom: 0, maxHeight: '70vh', overflowY: 'auto', zIndex: 41, background: 'var(--surface)', borderRadius: '14px 14px 0 0', boxShadow: '0 -4px 24px rgba(0,0,0,0.15)', padding: '12px 14px 20px' },
  backdrop: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.25)', zIndex: 40 },
  head:   { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' },
  title:  { fontSize: '13px', fontWeight: 700, color: 'var(--text)' },
  close:  { border: 'none', background: 'none', fontSize: '16px', color: 'var(--text-muted)', cursor: 'pointer' },
  sub:    { fontSize: '10px', fontWeight: 700, color: 'var(--text-muted)', fontFamily: "'DM Mono',monospace", textTransform: 'uppercase', letterSpacing: '0.06em', margin: '10px 0 4px' },
  row:    { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', padding: '4px 0', fontSize: '13px', color: 'var(--text)' },
  num:    { width: '90px', padding: '5px 8px', fontSize: '13px', fontFamily: "'DM Mono',monospace", border: '1px solid var(--border)', borderRadius: '6px', background: 'var(--bg)', color: 'var(--text)' },
  select: { padding: '5px 8px', fontSize: '13px', border: '1px solid var(--border)', borderRadius: '6px', background: 'var(--bg)', color: 'var(--text)' },
  colour: { width: '36px', height: '26px', padding: 0, border: '1px solid var(--border)', borderRadius: '6px', background: 'none', cursor: 'pointer' },
  check:  { width: '18px', height: '18px', accentColor: 'var(--accent)', margin: 0 },
  range:  { width: '120px', accentColor: 'var(--accent)' },
};

export default function IndicatorSettings({ study, mobile, onChange, onClose }) {
  if (!study) return null;
  const def = INDICATORS[study.id];
  const setParam = (k, v) => onChange({ params: { ...study.params, [k]: v } });
  const setStyle = (k, patch) => onChange({ styles: { ...study.styles, [k]: { ...study.styles[k], ...patch } } });

  const body = (
    <div style={mobile ? S.sheet : S.panel} onPointerDown={e => e.stopPropagation()}>
      <div style={S.head}>
        <span style={S.title}>{def.name} · {studyLabel(study)}</span>
        <button style={S.close} onClick={onClose} title="Close">✕</button>
      </div>
      {def.params.length > 0 && <div style={S.sub}>Inputs</div>}
      {def.params.map(p => (
        <label key={p.k} style={S.row}>
          {p.label}
          {p.source
            ? <select style={S.select} value={study.params[p.k]} onChange={e => setParam(p.k, e.target.value)}>
                {p.source.map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            : <input type="number" style={S.num} min={p.min} step={p.step} value={study.params[p.k]}
                     onChange={e => { const v = parseFloat(e.target.value); if (!Number.isNaN(v) && v >= (p.min ?? -Infinity)) setParam(p.k, p.step >= 1 ? Math.round(v) : v); }} />}
        </label>
      ))}
      <div style={S.sub}>Style</div>
      {def.outputs.map(o => {
        const st = study.styles[o.k] || {};
        return (
          <div key={o.k} style={S.row}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <input type="checkbox" style={S.check} checked={!st.hidden} onChange={e => setStyle(o.k, { hidden: !e.target.checked })} />
              {o.label}
            </label>
            {o.kind === 'hist'
              ? <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>green / red</span>
              : <span style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                  <input type="color" style={S.colour} value={st.color} onChange={e => setStyle(o.k, { color: e.target.value })} />
                  {o.kind === 'line' && (
                    <select style={S.select} value={st.width} onChange={e => setStyle(o.k, { width: Number(e.target.value) })}>
                      {[1, 1.5, 2, 3].map(w => <option key={w} value={w}>{w}px</option>)}
                    </select>
                  )}
                </span>}
          </div>
        );
      })}
      {def.fills?.length > 0 && (
        <label style={S.row}>
          Shading
          <input type="range" min="0" max="50" style={S.range} value={Math.round((study.fillOpacity ?? 0.08) * 100)}
                 onChange={e => onChange({ fillOpacity: Number(e.target.value) / 100 })} />
        </label>
      )}
    </div>
  );
  return mobile ? <><div style={S.backdrop} onClick={onClose} />{body}</> : body;
}
